import { Alert, Button, Card, Descriptions, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import PortalHomeworkCard, { type PortalHomework } from '@/pages/education/components/PortalHomeworkCard';
import PortalCustodyRestrictedAlert from '@/pages/education/components/PortalCustodyRestrictedAlert';
import { isPortalCustodyRestricted } from '@/pages/education/components/portalCustody';
import PortalAnnouncementsCard, {
  type PortalAnnouncements,
} from '@/pages/education/components/PortalAnnouncementsCard';

type Detail = {
  student: {
    admission_no: string;
    first_name: string;
    last_name?: string | null;
    status: string;
    campus_name?: string | null;
    relationship_name?: string | null;
  };
  enrollment?: { grade_name?: string; section_name?: string; session_name?: string; status?: string } | null;
  attendance?: { total_marks: number; by_status: Record<string, number>; window_days: number };
  recent_marks: Array<{ status_code: string; register_date: string; period_code?: string | null }>;
  fees?: { outstanding_amount: number; open_demands: number };
  fee_statement?: {
    read_only: true;
    lines: Array<{
      id: string;
      period_key: string;
      status: string;
      amount: number;
      currency_code: string;
      due_on: string | null;
    }>;
  };
  published_results?: {
    advisory: string;
    entries: Array<{ component_code: string; marks_raw: number | null; published_at: string | null }>;
  };
  homework?: PortalHomework;
  announcements?: PortalAnnouncements;
  links?: { family_statement_hint?: string };
};

export default function ParentPortalChildPage() {
  const { t } = useTranslation('education');
  const { studentId } = useParams();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['education-portal-parent-child', studentId],
    enabled: Boolean(studentId),
    queryFn: async () =>
      (await api.get<Detail>(`/education/portal/parent/children/${studentId}`)).data,
    // A custody denial is deterministic; do not hammer the API retrying it.
    retry: (failureCount, error) => !isPortalCustodyRestricted(error) && failureCount < 2,
  });
  const row = query.data;

  // FO-B13: custody-restricted guardian. Render the alert only: no student fields, cached or otherwise.
  if (query.isError && isPortalCustodyRestricted(query.error)) {
    return (
      <EducationPageChrome
        title={t('parentPortal')}
        description={t('parentPortalIntro')}
        extra={<Button onClick={() => navigate('/education/portal/parent')}>{t('parentPortal')}</Button>}
      >
        <PortalCustodyRestrictedAlert />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={
        row
          ? [row.student.first_name, row.student.last_name].filter(Boolean).join(' ')
          : t('parentPortal')
      }
      description={t('parentPortalIntro')}
      extra={
        <Button onClick={() => navigate('/education/portal/parent')}>{t('parentPortal')}</Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('portalGrantedOnlyHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('admissionNo')}>{row.student.admission_no}</Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag>{row.student.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('campus')}>{row.student.campus_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('relationship')}>
                {row.student.relationship_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('grade')}>
                {[row.enrollment?.grade_name, row.enrollment?.section_name].filter(Boolean).join(' / ') ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {row.enrollment?.session_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('feesOutstanding')}>
                {row.fees?.outstanding_amount ?? 0} ({row.fees?.open_demands ?? 0} {t('demands')})
              </Descriptions.Item>
              <Descriptions.Item label={t('attendance')}>
                {row.attendance
                  ? t('portalAttendanceSummary', {
                      total: row.attendance.total_marks,
                      days: row.attendance.window_days,
                    })
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        {row?.links?.family_statement_hint ? (
          <Alert type="warning" showIcon message={row.links.family_statement_hint} />
        ) : null}
        <Card
          size="small"
          title={t('portalAttendanceBreakdown', { days: row?.attendance?.window_days ?? 14 })}
          loading={query.isLoading}
        >
          {row?.attendance && row.attendance.total_marks > 0 ? (
            <Space wrap>
              {Object.entries(row.attendance.by_status).map(([code, count]) => (
                <Tag key={code}>
                  {code}: {count}
                </Tag>
              ))}
            </Space>
          ) : (
            '—'
          )}
        </Card>
        <PortalAnnouncementsCard announcements={row?.announcements} loading={query.isLoading} />
        <PortalHomeworkCard homework={row?.homework} studentId={studentId} loading={query.isLoading} />
        <Card size="small" title={t('portalFeeStatement')} loading={query.isLoading}>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('portalFeeStatementHint')} />
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: t('portalNoFeeLines') }}
            dataSource={row?.fee_statement?.lines ?? []}
            columns={[
              { title: t('portalPeriodKey'), dataIndex: 'period_key' },
              { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
              { title: t('amount'), dataIndex: 'amount', align: 'right' as const },
              { title: t('portalCurrency'), dataIndex: 'currency_code', width: 90 },
              { title: t('dueOn'), dataIndex: 'due_on', render: (v: string | null) => v || '—' },
            ]}
          />
        </Card>
        <Card size="small" title={t('portalPublishedResults')} loading={query.isLoading}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('portalPublishedResultsHint')}
          />
          <Table
            rowKey={(r) => `${r.component_code}-${r.published_at ?? ''}`}
            size="small"
            pagination={false}
            locale={{ emptyText: t('portalNoPublishedResults') }}
            dataSource={row?.published_results?.entries ?? []}
            columns={[
              { title: t('component'), dataIndex: 'component_code' },
              {
                title: t('portalMarks'),
                dataIndex: 'marks_raw',
                align: 'right' as const,
                render: (v: number | null) => (v == null ? '—' : v),
              },
              {
                title: t('portalPublishedAt'),
                dataIndex: 'published_at',
                render: (v: string | null) => (v ? v.slice(0, 10) : '—'),
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('recentAttendance')}>
          <Table
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={row?.recent_marks ?? []}
            columns={[
              { title: t('date'), dataIndex: 'register_date', width: 120 },
              { title: t('period'), dataIndex: 'period_code', width: 100, render: (v: string) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status_code',
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
